import {
    createRouter,
    createWebHistory
} from "vue-router";

import {
    useCurrentUser, useFirebaseAuth
} from "vuefire";

import {
    signOut
} from "firebase/auth";
import * as Sentry from "@sentry/vue";

import useUserStore from '@/stores/user.js'

// Detect if we're on the warehouse subdomain
const isWarehouseSubdomain = window.location.hostname.startsWith('warehouse.')

// Helper to check warehouse session
function getWarehouseSession() {
    try {
        const session = localStorage.getItem('warehouseSession')
        return session ? JSON.parse(session) : null
    } catch {
        return null
    }
}

const routes = [{
        path: "/",
        name: "Dashboard",
        component: () => import("@/views/Dashboard/Dashboard.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Dashboard" }
    },
    {
        path: "/login",
        name: "Login",
        component: () => import("@/views/Auth/Login.vue"),
        meta: { requires_no_auth: true, title: "Login" }
    },
    {
        path: "/forgot",
        name: "Forgot",
        component: () => import("@/views/Auth/Forgot.vue"),
        meta: { requires_no_auth: true, title: "Forgot Password" }
    },
    // {
    //     path: "/register",
    //     name: "Register",
    //     component: () => import("@/views/Auth/Register.vue"),
    //     meta: { requires_no_auth: true, title: "Register" }
    // },
    {
        path: "/logout",
        name: "Logout",
        meta: { logout: true }
    },
    {
        path: "/override",
        name: "Override",
        component: () => import("@/views/Auth/Override.vue"),
        meta: { requires_no_auth: true, title: "Override" }
    },
    {
        path: "/amazon-connect",
        name: "AmazonConnect",
        component: () => import("@/views/Auth/AmazonConnect.vue"),
        meta: { auth_no_preference: true, title: "Connect Amazon Account" }
    },
    {
        path: "/shipments/",
        name: "Shipments",
        component: () => import("@/views/Shipments/Shipments.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Shipments" }
    },
    {
        path: "/shipments/inbound/",
        name: "ShipmentsInbound",
        component: () => import("@/views/Shipments/Inbound/Inbound.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Inbound Shipments" }
    },
    {
        path: "/shipments/inbound/create",
        name: "ShipmentsInboundCreate",
        component: () => import("@/views/Shipments/Inbound/Create.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Create Inbound Shipment" }
    },
    {
        path: "/shipments/inbound/:id",
        name: "ShipmentsInboundDetail",
        component: () => import("@/views/Shipments/Inbound/Detail.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Inbound Shipment" }
    },
    {
        path: "/shipments/outbound/",
        name: "ShipmentsOutbound",
        component: () => import("@/views/Shipments/Outbound/Outbound.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Outbound Shipments" }
    },
    {
        path: "/shipments/outbound/create",
        name: "ShipmentsOutboundCreate",
        component: () => import("@/views/Shipments/Outbound/Create.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Create Outbound Shipment" }
    },
                {
    path: "/shipments/amazon",
    name: "ShipmentsAmazonFBA",
    component: () => import("@/views/Shipments/AmazonFBA.vue"),
    meta: { requires_auth: true, requires_premium: true, title: "Amazon FBA Shipments" }
},
    {
        path: "/shipments/outbound/create-fba",
        name: "ShipmentsOutboundCreateFBA",
        component: () => import("@/views/Shipments/Outbound/CreateFBA.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Create FBA Shipment" }
    },
    {
        path: "/shipments/outbound/:id",
        name: "ShipmentsOutboundDetail",
        component: () => import("@/views/Shipments/Outbound/Detail.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Outbound Shipment" }
    },
    {
        path: "/requests/",
        name: "Requests",
        component: () => import("@/views/Requests/Requests.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Requests" }
    },
    {
        path: "/requests/inspection",
        name: "RequestsInspection",
        component: () => import("@/views/Requests/Inspection.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Inspection Request" }
    },
    {
        path: "/requests/return",
        name: "RequestsReturn",
        component: () => import("@/views/Requests/Return.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Return Request" }
    },
    {
        path: "/requests/forward",
        name: "RequestsForward",
        component: () => import("@/views/Requests/Forward.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Forward Request" }
    },
    {
        path: "/requests/disposal",
        name: "RequestsDisposal",
        component: () => import("@/views/Requests/Disposal.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Disposal Request" }
    },
    {
        path: "/requests/:id",
        name: "RequestsDetail",
        component: () => import("@/views/Requests/Detail.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Request" }
    },
    {
        path: "/inventory",
        name: "Inventory",
        component: () => import("@/views/Inventory/Inventory.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Inventory" }
    },
    {
        path: "/inventory/create",
        name: "InventoryCreate",
        component: () => import("@/views/Inventory/Create.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Create Inventory Item" }
    },
    {
        path: "/inventory/:id",
        name: "InventoryDetail",
        component: () => import("@/views/Inventory/Detail.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Inventory Item" }
    },
    {
        path: "/billing/",
        name: "Billing",
        component: () => import("@/views/Billing/Billing.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Billing" }
    },
    {
        path: "/billing/invoices/",
        name: "BillingInvoices",
        component: () => import("@/views/Billing/Invoices/Invoices.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Invoices" }
    },
    {
        path: "/billing/invoices/:id",
        name: "BillingInvoicesDetail",
        component: () => import("@/views/Billing/Invoices/Invoice.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Invoice" }
    },
    {
        path: "/settings/",
        name: "Settings",
        component: () => import("@/views/Settings/Settings.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Settings" }
    },
    {
        path: "/settings/profile",
        name: "SettingsProfile",
        component: () => import("@/views/Settings/Profile.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Profile" }
    },
    {
        path: "/settings/notifications",
        name: "SettingsNotifications",
        component: () => import("@/views/Settings/Notifications.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Notifications" }
    },
    {
        path: "/settings/account",
        name: "SettingsAccount",
        component: () => import("@/views/Settings/Account.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Account" }
    },
    {
        path: "/settings/integrations",
        name: "SettingsIntegrations",
        component: () => import("@/views/Settings/Integrations.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Integrations" }
    },
    // ==========================================
    // SERVICES (feature-flagged per client)
    // ==========================================
    {
        path: "/services",
        name: "Services",
        redirect: "/services/orders",
        meta: { requires_auth: true, requires_premium: true }
    },
                   {
        path: "/services/requests",
        name: "ServicesRequests",
        component: () => import("@/views/services/ServiceRequests.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Service Requests" }
    },
    {
        path: "/services/orders",
        name: "ServicesOrders",
        component: () => import("@/views/services/OrderList.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Ordering Service" }
    },
    {
        path: "/services/orders/create",
        name: "ServicesOrderCreate",
        component: () => import("@/views/services/OrderCreate.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "New Order Request" }
    },
    {
        path: "/services/orders/:id",
        name: "ServicesOrderDetail",
        component: () => import("@/views/services/OrderDetail.vue"),
        meta: { requires_auth: true, requires_premium: true, title: "Order Detail" }
    },
    {
        path: "/onboarding",
        name: "OnboardingDefault",
        meta: { redirect: "OnboardingAccount" }
    },
    {
        path: "/onboarding/step1",
        name: "OnboardingAccount",
        component: () => import("@/views/Onboarding/Account.vue"),
        meta: { requires_no_auth: true, title: "Create Account" }
    },
    {
        path: "/onboarding/step2",
        name: "OnboardingDiscord",
        component: () => import("@/views/Onboarding/Discord.vue"),
        meta: { requires_auth: true, redirect_if_no_auth: "OnboardingAccount", title: "Connect Discord" }
    },
    {
        path: "/onboarding/step3",
        name: "OnboardingSurvey",
        component: () => import("@/views/Onboarding/Survey.vue"),
        meta: { requires_auth: true, redirect_if_no_auth: "OnboardingAccount", title: "Quick Survey" }
    },
    {
        path: "/onboarding/step4",
        name: "OnboardingPayment",
        component: () => import("@/views/Onboarding/Payment.vue"),
        meta: { requires_auth: true, redirect_if_no_auth: "OnboardingAccount", title: "Payment Setup" }
    },
    {
        path: "/onboarding/step5",
        name: "OnboardingSuccess",
        component: () => import("@/views/Onboarding/Success.vue"),
        meta: { requires_auth: true, redirect_if_no_auth: "OnboardingAccount", title: "Welcome!" }
    },
    {
        path: "/error/400",
        name: "400",
        component: () => import("@/views/Error/400.vue"),
        meta: { auth_no_preference: true }
    },
    {
        path: "/error/500",
        name: "500",
        component: () => import("@/views/Error/500.vue"),
        meta: { auth_no_preference: true }
    },
    {
        path: "/error/503",
        name: "503",
        component: () => import("@/views/Error/503.vue"),
        meta: { auth_no_preference: true }
    },
    // ==========================================
    // WAREHOUSE ROUTES (warehouse.prepworx.io)
    // ==========================================
    {
        path: "/warehouse/login",
        name: "WarehouseLogin",
        component: () => import("@/views/warehouse/WarehouseGate.vue"),
        meta: { warehouse_route: true, warehouse_no_session: true, title: "Warehouse Login" }
    },
                {
        path: "/warehouse/service-requests",
        name: "PrepWorxServiceRequests",
        component: () => import("@/views/warehouse/ServiceRequestsAdmin.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Service Requests" }
    },
                {
    path: "/warehouse/returns",
    name: "WarehouseReturns",
    component: () => import("@/views/warehouse/ReturnsModule.vue"),
    meta: { warehouse_route: true, requires_warehouse_session: true, title: "Returns" }
},
    {
        path: "/warehouse/logout",
        name: "WarehouseLogout",
        meta: { warehouse_logout: true }
    },
    {
        path: "/warehouse",
        name: "WarehouseHome",
        component: () => import("@/views/warehouse/WarehouseHome.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Warehouse" }
    },
    {
        path: "/warehouse/buy-label",
        name: "PrepWorxBuyLabel",
        component: () => import("@/views/warehouse/BuyShippingLabel.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Buy Shipping Label" }
    },
    {
        path: "/warehouse/shipments",
        name: "PrepWorxShipments",
        component: () => import("@/views/warehouse/ShipmentList.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Shipments" }
    },
    {
        path: "/warehouse/pick/:id",
        name: "PrepWorxPicking",
        component: () => import("@/views/warehouse/PickingStation.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Picking" }
    },
    {
        path: "/warehouse/pack/:id",
        name: "PrepWorxPacking",
        component: () => import("@/views/warehouse/PackingStation.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Packing" }
    },
    {
        path: "/warehouse/ship/:id",
        name: "PrepWorxShipping",
        component: () => import("@/views/warehouse/ShipStation.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Shipping" }
    },
    {
        path: "/warehouse/outbound-bill/:id",
        name: "PrepWorxOutboundBilling",
        component: () => import("@/views/warehouse/OutboundBilling.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Outbound Billing" }
    },
                {
        path: "/warehouse/bill-services",
        name: "PrepWorxBillServices",
        component: () => import("@/views/warehouse/BillServices.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Bill Services" }
    },
    {
        path: "/warehouse/storage",
        name: "PrepWorxStorage",
        component: () => import("@/views/warehouse/Storage.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Storage" }
    },
    {
        path: "/warehouse/inventory",
        name: "PrepWorxInventory",
        component: () => import("@/views/warehouse/WarehouseInventory.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Inventory" }
    },
    {
        path: "/warehouse/unbox-label",
        name: "PrepWorxUnboxLabel",
        component: () => import("@/views/warehouse/UnboxLabelStation.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Unbox & Label" }
    },
    {
        path: "/warehouse/picklist-packing",
        name: "PrepWorxPicklistPacking",
        component: () => import("@/views/warehouse/PickListPackingStation.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Pick List Packing" }
    },
    {
        path: "/warehouse/create-fba",
        name: "WarehouseCreateFBA",
        component: () => import("@/views/warehouse/WarehouseCreateFBA.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Create FBA Shipment" }
    },
                
    {
        path: "/warehouse/bulk-inbound",
        name: "BulkInboundScanner",
        component: () => import("@/views/warehouse/BulkInboundScanner.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Bulk Inbound" }
    },
    {
        path: "/warehouse/manual-inbound",
        name: "PrepWorxManualInbound",
        component: () => import("@/views/warehouse/ManualInbound.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Manual Inbound" }
    },
    {
        path: "/warehouse/inbound",
        name: "InboundShipments",
        component: () => import("@/views/warehouse/InboundShipments.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Inbound Shipments" }
    },

                {
        path: "/warehouse/saved-sessions",
        name: "SavedSessions",
        component: () => import("@/views/warehouse/SavedSessions.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, title: "Saved Sessions" }
    },
    // ==========================================
    // ADMIN ROUTES (warehouse.prepworx.io/warehouse/admin/*)
    // Requires warehouse session + isAdmin flag
    // ==========================================
    {
        path: "/warehouse/admin/clients",
        name: "AdminClients",
        component: () => import("@/views/warehouse/admin/ClientManagement.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "Client Management" }
    },
    {
        path: "/warehouse/admin/storage",
        name: "PrepWorxStorageDashboard",
        component: () => import("@/views/warehouse/admin/StorageDashboard.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "Storage Dashboard" }
    },

     {
        path: "/warehouse/admin/revenue",
        name: "AdminRevenue",
        component: () => import("@/views/warehouse/admin/RevenueDashboard.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "Revenue Dashboard" }
    },

    {
        path: "/warehouse/admin/order-queue",
        name: "AdminOrderQueue",
        component: () => import("@/views/warehouse/admin/AdminOrderQueue.vue"),
        meta: { warehouse_route: true, requires_warehouse_session: true, requires_admin: true, title: "Order Queue" }
    },
                
    {
        path: "/:pathMatch(.*)*",
        name: "NotFound",
        component: () => import("@/views/Error/404.vue"),
        meta: { auth_no_preference: true }
    }
]

const router = createRouter({
    history: createWebHistory(
        import.meta.env.BASE_URL),
    routes
});

router.beforeEach(async (to, from, next) => {
    const userStore = useUserStore()
    document.title = "PrepWorx - " + (to.meta.title || "PrepWorx");

    // ==========================================
    // WAREHOUSE SUBDOMAIN HANDLING
    // ==========================================
    if (isWarehouseSubdomain) {
        const warehouseSession = getWarehouseSession()
        const isWarehouseRoute = to.meta.warehouse_route || false
        const requiresWarehouseSession = to.meta.requires_warehouse_session || false
        const warehouseNoSession = to.meta.warehouse_no_session || false
        const warehouseLogout = to.meta.warehouse_logout || false
        const requiresAdmin = to.meta.requires_admin || false

        // Handle logout - use localStorage (not sessionStorage)
        if (warehouseLogout) {
            localStorage.removeItem('warehouseSession')
            next({ name: 'WarehouseLogin' })
            return
        }

        // If on warehouse subdomain but trying to access client routes, redirect to warehouse login
        if (!isWarehouseRoute && !to.meta.auth_no_preference) {
            if (warehouseSession) {
                next({ name: 'WarehouseHome' })
            } else {
                next({ name: 'WarehouseLogin' })
            }
            return
        }

        // Warehouse route that requires session but none exists
        if (requiresWarehouseSession && !warehouseSession) {
            next({ name: 'WarehouseLogin' })
            return
        }

        // Admin route but user is not admin — redirect to warehouse home
        if (requiresAdmin && (!warehouseSession || !warehouseSession.isAdmin)) {
            next({ name: 'WarehouseHome' })
            return
        }

        // Already logged in but trying to access login page
        if (warehouseNoSession && warehouseSession) {
            next({ name: 'WarehouseHome' })
            return
        }

        // All good for warehouse routes
        next()
        return
    }

    // ==========================================
    // CLIENT APP (app.prepworx.io) - Original Logic
    // ==========================================
    
    // Block warehouse routes on client subdomain
    if (to.meta.warehouse_route) {
        next({ name: 'Dashboard' })
        return
    }

    const requires_auth = to.meta.requires_auth || false;
    const auth_no_preference = to.meta.auth_no_preference || false;
    const requires_premium = to.meta.requires_premium || false;
    const requires_no_auth = to.meta.requires_no_auth || false;
    const redirect_if_auth = to.meta.redirect_if_auth || "";
    const redirect_if_no_auth = to.meta.redirect_if_no_auth || "";
    const logout = to.meta.logout || false;
    const redirect = to.meta.redirect || false;
    const redirect_url = to.meta.redirect_url || false;
    
    if (requires_premium && userStore.stateData.isLoggedIn && (!userStore.stateData.claims.hasOwnProperty("stripeRole") || (userStore.stateData.claims.stripeRole || "") != "premium")) {
        // Send non-premium users to the appropriate onboarding step
        // instead of always dumping them at Discord (step 2)
        next({ name: "OnboardingPayment" })
        return
    }
    if (logout) {
        if (userStore.stateData.isLoggedIn) {
            Sentry.setUser(null);
            OpenReplay.setUserID("")
            userStore.clear() 
            signOut(useFirebaseAuth())
        }
        window.location = router.resolve({
            name: "Login"
        }).href
    }
    else if (requires_auth && !userStore.stateData.isLoggedIn) {
        next({ name: "Login" })
    }
    else if (requires_no_auth && redirect_if_auth != "" && userStore.stateData.isLoggedIn) {
        next({ name: redirect_if_auth })
    }
    else if (requires_auth && redirect_if_no_auth != "" && !userStore.stateData.isLoggedIn) {
        next({ name: redirect_if_no_auth })
    }
    else if (requires_no_auth && userStore.stateData.isLoggedIn) {
        next({ name: "Dashboard" })
    }
    else if (redirect) {
        next({ name: redirect })
    }
    else if (redirect_url) {
        window.location = redirect_url
    }
    else {
        next()
    }
})

router.onError((error, to) => {
    if (error.message.includes('Failed to fetch dynamically imported module')) {
        window.location = to.fullPath
    }
})

// Export helper for components to access warehouse session
export function useWarehouseSession() {
    return getWarehouseSession()
}

export default router;
